import { onMounted, onUnmounted, ref, getCurrentInstance, watch, } from '@vue/composition-api';
import { Editor } from '@tiptap/vue-2';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Mention from '@tiptap/extension-mention';
import { suggestion } from '@/components/WysiwygInput/suggestion';
import { MENTION_CLASS_NAME } from '@/components/WysiwygInput/wysiwyg.constants';
import { isUrlValid } from '@/util/validation';
import { Node } from '@tiptap/core';
const EmbeddedOffer = (onOfferClick) => Node.create({
    name: 'embeddedOffer',
    group: 'inline',
    inline: true,
    content: 'text*',
    addAttributes() {
        return {
            'data-offer-id': {
                default: null,
                parseHTML: (element) => element.getAttribute('data-offer-id'),
                renderHTML: (attributes) => {
                    if (!attributes['data-offer-id']) {
                        return {};
                    }
                    return {
                        'data-offer-id': attributes['data-offer-id'],
                    };
                },
            },
            'data-offer-type': {
                default: null,
                parseHTML: (element) => element.getAttribute('data-offer-type'),
                renderHTML: (attributes) => {
                    if (!attributes['data-offer-type']) {
                        return {};
                    }
                    return {
                        'data-offer-type': attributes['data-offer-type'],
                    };
                },
            },
            'data-offer-title': {
                default: null,
                parseHTML: (element) => element.getAttribute('data-offer-title'),
                renderHTML: (attributes) => {
                    if (!attributes['data-offer-title']) {
                        return {};
                    }
                    return {
                        'data-offer-title': attributes['data-offer-title'],
                    };
                },
            },
            'data-offer-internal-number': {
                default: null,
                parseHTML: (element) => element.getAttribute('data-offer-internal-number'),
                renderHTML: (attributes) => {
                    if (!attributes['data-offer-internal-number']) {
                        return {};
                    }
                    return {
                        'data-offer-internal-number': attributes['data-offer-internal-number'],
                    };
                },
            },
            'data-offer-created-at': {
                default: null,
                parseHTML: (element) => element.getAttribute('data-offer-created-at'),
                renderHTML: (attributes) => {
                    if (!attributes['data-offer-created-at']) {
                        return {};
                    }
                    return {
                        'data-offer-created-at': attributes['data-offer-created-at'],
                    };
                },
            },
            class: {
                default: 'embedded-offer',
                parseHTML: (element) => element.getAttribute('class'),
                renderHTML: (attributes) => {
                    return {
                        class: attributes.class || 'embedded-offer',
                    };
                },
            },
        };
    },
    parseHTML() {
        return [
            {
                tag: 'span[data-offer-id]',
                getAttrs: (element) => {
                    if (typeof element === 'string' ||
                        !(element instanceof HTMLElement)) {
                        return false;
                    }
                    return {
                        'data-offer-id': element.getAttribute('data-offer-id'),
                        'data-offer-type': element.getAttribute('data-offer-type'),
                        'data-offer-title': element.getAttribute('data-offer-title'),
                        'data-offer-internal-number': element.getAttribute('data-offer-internal-number'),
                        'data-offer-created-at': element.getAttribute('data-offer-created-at'),
                        class: element.getAttribute('class'),
                    };
                },
            },
        ];
    },
    renderHTML({ HTMLAttributes }) {
        return ['span', HTMLAttributes, 0];
    },
    addNodeView() {
        return ({ node, HTMLAttributes }) => {
            const container = document.createElement('span');
            Object.entries(HTMLAttributes).forEach(([key, value]) => {
                if (value !== null && value !== undefined) {
                    container.setAttribute(key, value);
                }
            });
            const offerTitle = container.getAttribute('data-offer-title') || '';
            const internalNumber = container.getAttribute('data-offer-internal-number') || '';
            const createdAt = container.getAttribute('data-offer-created-at') || '';
            let formattedDate = '';
            if (createdAt) {
                try {
                    const date = new Date(createdAt);
                    formattedDate = date.toLocaleDateString();
                }
                catch (e) {
                    formattedDate = createdAt;
                }
            }
            const titleSpan = document.createElement('span');
            titleSpan.className = 'embedded-offer-title';
            titleSpan.textContent = `Offer - ${offerTitle}`;
            container.appendChild(titleSpan);
            if (internalNumber && formattedDate) {
                const captionSpan = document.createElement('span');
                captionSpan.className = 'embedded-offer-caption';
                captionSpan.textContent = `${internalNumber}, ${formattedDate}`;
                container.appendChild(captionSpan);
            }
            container.addEventListener('click', (e) => {
                e.preventDefault();
                const offerId = container.getAttribute('data-offer-id');
                const offerType = container.getAttribute('data-offer-type');
                if (offerId && offerType && onOfferClick) {
                    onOfferClick(offerId, offerType);
                }
            });
            return {
                dom: container,
            };
        };
    },
});
export const useTextEditor = (ctx, { content, readonly, placeholder, contentClass, getMentionSuggestions, onOfferClick, }) => {
    const editor = ref(null);
    const mentionList = ref(null);
    onMounted(() => {
        const extensions = ref([
            StarterKit,
            EmbeddedOffer(onOfferClick),
        ]);
        const instance = getCurrentInstance();
        if (!instance) {
            return;
        }
        if (placeholder) {
            extensions.value.push(Placeholder.configure({
                placeholder,
            }));
        }
        extensions.value.push(Link.configure({
            HTMLAttributes: {
                class: 'text-blue underline',
            },
            validate: (href) => isUrlValid(href),
        }));
        const { suggestionConfig, mentionComponent } = suggestion(instance.proxy, getMentionSuggestions);
        extensions.value.push(Mention.configure({
            HTMLAttributes: {
                class: MENTION_CLASS_NAME,
            },
            // @ts-ignore
            suggestion: suggestionConfig,
        }));
        mentionList.value = mentionComponent;
        mentionList.value.$on('mention', (e) => {
            ctx.emit('mention', e);
        });
        editor.value = new Editor({
            content: content.value,
            editable: !readonly.value,
            extensions: extensions.value,
            parseOptions: {
                preserveWhitespace: true,
            },
            editorProps: {
                attributes: {
                    class: contentClass.value,
                },
                transformPastedHTML(html) {
                    return html;
                },
            },
        });
    });
    onUnmounted(() => {
        mentionList.value?.$destroy();
        editor.value?.destroy();
    });
    watch(content, () => {
        if (content.value !== editor.value?.getHTML()) {
            editor.value?.commands.setContent(content.value, false);
        }
    });
    watch(readonly, () => {
        editor.value?.setEditable(!readonly.value);
    });
    watch(contentClass, () => {
        editor.value?.setOptions({
            editorProps: { attributes: { class: contentClass.value } },
        });
    });
    return editor;
};
